import React, { useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  SpreadsheetComponent,
  SheetsDirective,
  SheetDirective,
  Inject,
  Ribbon,
  FormulaBar,
  SheetTabs,
  Selection,
  Edit,
  Open,
  Save,
  ContextMenu,
  NumberFormat,
  Formula,
} from '@syncfusion/ej2-react-spreadsheet';
import { getApiBaseUrl } from '../utils/apiUrl';

// Syncfusion CSS importieren
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-react-spreadsheet/styles/material.css';

// Syncfusion Lizenz registrieren (Community-Lizenz oder Trial)
import { registerLicense } from '@syncfusion/ej2-base';

// WICHTIG: Ersetzen Sie dies mit Ihrem Syncfusion-Lizenzschlüssel
// Kostenlose Community-Lizenz: https://www.syncfusion.com/products/communitylicense
const SYNCFUSION_LICENSE_KEY = process.env.REACT_APP_SYNCFUSION_LICENSE_KEY || '';
if (SYNCFUSION_LICENSE_KEY) {
  registerLicense(SYNCFUSION_LICENSE_KEY);
}

interface SyncfusionSpreadsheetProps {
  documentId?: number;
  height?: string;
  onSave?: (blob: Blob) => void;
  onContentChange?: () => void;
}

export interface SyncfusionSpreadsheetHandle {
  getDocumentAsBlob: () => Promise<Blob | null>;
  saveDocument: () => Promise<void>;
}

const SyncfusionSpreadsheet = forwardRef<SyncfusionSpreadsheetHandle, SyncfusionSpreadsheetProps>(
  ({ documentId, height = '600px', onSave, onContentChange }, ref) => {
    const spreadsheetRef = useRef<SpreadsheetComponent>(null);
    const [isEditorReady, setIsEditorReady] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const documentIdRef = useRef<number | undefined>(documentId);

    // Update ref when documentId changes
    useEffect(() => {
      documentIdRef.current = documentId;
    }, [documentId]);

    // Editor-Methoden nach außen exponieren
    useImperativeHandle(ref, () => ({
      // Dokument als Blob exportieren
      getDocumentAsBlob: async (): Promise<Blob | null> => {
        if (spreadsheetRef.current) {
          return new Promise((resolve) => {
            // saveAsBlob ist verfügbar in Syncfusion
            spreadsheetRef.current!.saveAsJson().then((json: any) => {
              // JSON zu Blob konvertieren
              const blob = new Blob([JSON.stringify(json)], { type: 'application/json' });
              resolve(blob);
            }).catch(() => {
              resolve(null);
            });
          });
        }
        return null;
      },

      // Dokument speichern
      saveDocument: async () => {
        if (spreadsheetRef.current && documentIdRef.current) {
          try {
            const apiUrl = getApiBaseUrl();

            // Spreadsheet als JSON exportieren
            const jsonResult = await spreadsheetRef.current.saveAsJson();
            console.log('SyncfusionSpreadsheet: JSON exportiert', typeof jsonResult, jsonResult ? Object.keys(jsonResult) : 'null');

            // Wir speichern das gesamte Ergebnis von saveAsJson()
            // Das Format ist: { jsonObject: { Workbook: ... } }
            // Beim Laden extrahieren wir jsonObject und übergeben es an openFromJson({ file: ... })

            // JSON an Backend senden zum Speichern
            const response = await fetch(`${apiUrl}/api/syncfusion/SaveSpreadsheetFromJson/${documentIdRef.current}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(jsonResult),
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error('SyncfusionSpreadsheet: Server-Fehler:', errorText);
              throw new Error(`Speichern fehlgeschlagen: ${response.status}`);
            }

            console.log('SyncfusionSpreadsheet: Dokument erfolgreich gespeichert');
          } catch (error) {
            console.error('SyncfusionSpreadsheet: Fehler beim Speichern:', error);
            throw error;
          }
        }
      },
    }));

    // Dokument laden wenn documentId angegeben ist
    useEffect(() => {
      const loadDocument = async () => {
        if (!isEditorReady || !spreadsheetRef.current || !documentId) {
          console.log('SyncfusionSpreadsheet: Editor noch nicht bereit oder keine documentId');
          return;
        }

        setIsLoading(true);
        const apiUrl = getApiBaseUrl();

        try {
          console.log('SyncfusionSpreadsheet: Lade Dokument mit ID', documentId);

          // Neuer Endpoint: Lädt direkt anhand der dokumentId
          // Prüft automatisch ob eine gespeicherte JSON-Datei existiert
          const openResponse = await fetch(`${apiUrl}/api/syncfusion/OpenSpreadsheetDocument/${documentId}`);

          if (!openResponse.ok) {
            throw new Error(`Laden fehlgeschlagen: ${openResponse.status}`);
          }

          const jsonData = await openResponse.json();
          console.log('SyncfusionSpreadsheet: JSON-Daten erhalten', typeof jsonData, Object.keys(jsonData));

          // JSON in Spreadsheet laden
          // Das JSON kann verschiedene Formate haben:
          // 1. Von saveAsJson() gespeichert: { jsonObject: { Workbook: ... } }
          // 2. Von Workbook.Open(): { Workbook: ... } (direkt)
          // 3. Von Workbook.Open() als String: String der JSON enthält
          // openFromJson erwartet { file: { Workbook: ... } }
          let fileContent = jsonData;
          if (jsonData.jsonObject) {
            // Format 1: Extrahiere das jsonObject
            fileContent = jsonData.jsonObject;
          } else if (typeof jsonData === 'string') {
            // Format 3: String parsen
            try {
              fileContent = JSON.parse(jsonData);
              if (fileContent.jsonObject) {
                fileContent = fileContent.jsonObject;
              }
            } catch (e) {
              console.error('SyncfusionSpreadsheet: Fehler beim Parsen des JSON-Strings:', e);
            }
          }

          console.log('SyncfusionSpreadsheet: Lade mit fileContent-Keys:', Object.keys(fileContent));
          await spreadsheetRef.current.openFromJson({ file: fileContent });

          console.log('SyncfusionSpreadsheet: Dokument erfolgreich geladen');
        } catch (error) {
          console.error('SyncfusionSpreadsheet: Fehler beim Laden des Dokuments:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadDocument();
    }, [documentId, isEditorReady]);

    // Editor bereit-Callback
    const handleCreated = () => {
      console.log('SyncfusionSpreadsheet: Editor ist bereit');
      setIsEditorReady(true);
    };

    // Änderungs-Handler
    const handleCellEdit = () => {
      if (onContentChange) {
        onContentChange();
      }
    };

    // Speichern-Funktion für den Button
    const handleSaveClick = async () => {
      if (!spreadsheetRef.current || !documentIdRef.current) {
        alert('Kein Dokument zum Speichern vorhanden');
        return;
      }

      try {
        const apiUrl = getApiBaseUrl();

        // Spreadsheet als JSON exportieren
        // saveAsJson() gibt ein Objekt mit jsonObject zurück
        const jsonResult = await spreadsheetRef.current.saveAsJson() as any;
        console.log('SyncfusionSpreadsheet: JSON exportiert für Speichern (Button)', typeof jsonResult);

        // Das jsonObject extrahieren - das ist das eigentliche Workbook
        // und zirkuläre Referenzen mit einem Replacer behandeln
        let jsonString: string;
        try {
          const workbookData = (jsonResult as any).jsonObject || jsonResult;
          // Verwende einen Set um zirkuläre Referenzen zu erkennen
          const seen = new WeakSet();
          jsonString = JSON.stringify(workbookData, (key, value) => {
            if (typeof value === 'object' && value !== null) {
              if (seen.has(value)) {
                return undefined; // Zirkuläre Referenz entfernen
              }
              seen.add(value);
            }
            return value;
          });
        } catch (stringifyError) {
          console.error('SyncfusionSpreadsheet: Fehler beim Serialisieren:', stringifyError);
          throw new Error('Fehler beim Serialisieren des Dokuments');
        }

        console.log('SyncfusionSpreadsheet: JSON-String Länge:', jsonString.length);

        // JSON an Backend senden zum Speichern
        const response = await fetch(`${apiUrl}/api/syncfusion/SaveSpreadsheetFromJson/${documentIdRef.current}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: jsonString,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('SyncfusionSpreadsheet: Server-Fehler:', errorText);
          throw new Error(`Speichern fehlgeschlagen: ${response.status}`);
        }

        alert('Dokument erfolgreich gespeichert!');
      } catch (error) {
        console.error('SyncfusionSpreadsheet: Fehler beim Speichern:', error);
        alert('Fehler beim Speichern: ' + error);
      }
    };

    return (
      <div style={{ height, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Speichern-Button über dem Editor wenn documentId vorhanden */}
        {documentId && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '8px',
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #ddd'
          }}>
            <button
              onClick={handleSaveClick}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Speichern
            </button>
          </div>
        )}
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000,
          }}>
            <span>Lade Excel-Dokument...</span>
          </div>
        )}
        <SpreadsheetComponent
          ref={spreadsheetRef}
          height="100%"
          locale="de"
          openUrl={`${getApiBaseUrl()}/api/syncfusion/OpenSpreadsheet`}
          saveUrl={`${getApiBaseUrl()}/api/syncfusion/SaveSpreadsheet`}
          allowOpen={true}
          allowSave={true}
          allowEditing={true}
          allowNumberFormatting={true}
          allowCellFormatting={true}
          allowSorting={true}
          allowFiltering={true}
          allowUndoRedo={true}
          allowFreezePane={true}
          allowWrap={true}
          showRibbon={true}
          showFormulaBar={true}
          showSheetTabs={true}
          created={handleCreated}
          cellEdit={handleCellEdit}
        >
          <SheetsDirective>
            <SheetDirective name="Tabelle1">
            </SheetDirective>
          </SheetsDirective>
          <Inject services={[
            Ribbon,
            FormulaBar,
            SheetTabs,
            Selection,
            Edit,
            Open,
            Save,
            ContextMenu,
            NumberFormat,
            Formula
          ]} />
        </SpreadsheetComponent>
      </div>
    );
  }
);

SyncfusionSpreadsheet.displayName = 'SyncfusionSpreadsheet';

export default SyncfusionSpreadsheet;
